import React from "react";
import axios from "axios";
import { Button, Empty, Select, Spin } from "antd";
import { DeleteOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";

import { makeBlockSku } from "./blockItems";

const SEARCH_URL = "/api/v1/products/simple_list";

const useSkuSearch = () => {
  const [options, setOptions] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const requestId = React.useRef(0);

  const search = React.useCallback((term) => {
    const query = (term || "").trim();
    if (query.length < 3) {
      setOptions([]);
      setSearching(false);
      return;
    }

    const current = requestId.current + 1;
    requestId.current = current;
    setSearching(true);

    axios
      .post(SEARCH_URL, {
        q: { title_or_identifier_cont: query },
        per_page: 20,
      })
      .then((response) => {
        if (requestId.current !== current) return;
        setOptions(response.data?.data || []);
      })
      .catch(() => {
        if (requestId.current !== current) return;
        setOptions([]);
      })
      .finally(() => {
        if (requestId.current === current) setSearching(false);
      });
  }, []);

  return { options, searching, search };
};

const useSkuTitles = (skus) => {
  const [titles, setTitles] = React.useState({});
  const missing = skus
    .map((entry) => entry.sku)
    .filter((sku) => sku && !(sku in titles));
  const missingKey = missing.join(",");

  React.useEffect(() => {
    if (!missing.length) return;

    axios
      .post(SEARCH_URL, {
        q: { identifier_in: missing },
        per_page: missing.length,
      })
      .then((response) => {
        const found = {};
        (response.data?.data || []).forEach((product) => {
          found[product.identifier] = product.title;
        });
        setTitles((prev) => {
          const next = { ...prev };
          missing.forEach((sku) => {
            next[sku] = found[sku] || null;
          });
          return next;
        });
      })
      .catch(() => {
        setTitles((prev) => {
          const next = { ...prev };
          missing.forEach((sku) => {
            next[sku] = null;
          });
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingKey]);

  return titles;
};

const SkuRow = ({ sku, title, index, total, onMove, onRemove }) => (
  <div
    className="d-flex align-items-center px-2 py-2"
    style={{ gap: 8, borderBottom: "1px solid #F0F0F0" }}
  >
    <span className="text-muted" style={{ minWidth: 18, fontSize: 12 }}>
      {index + 1}.
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="text-black" style={{ fontSize: 13 }}>
        {sku}
      </div>
      <div
        className="text-muted text-truncate"
        style={{ fontSize: 11 }}
        title={title || undefined}
      >
        {title === undefined ? "…" : title || "Prekė nerasta"}
      </div>
    </div>
    <UpOutlined
      style={{
        color: index === 0 ? "#D9D9D9" : "#454545",
        cursor: index === 0 ? "not-allowed" : "pointer",
      }}
      onClick={() => index > 0 && onMove(-1)}
    />
    <DownOutlined
      style={{
        color: index === total - 1 ? "#D9D9D9" : "#454545",
        cursor: index === total - 1 ? "not-allowed" : "pointer",
      }}
      onClick={() => index < total - 1 && onMove(1)}
    />
    <DeleteOutlined
      style={{ color: "#C94747", cursor: "pointer" }}
      onClick={onRemove}
    />
  </div>
);

const BlockSkus = ({ skus = [], itemsLimit, onChange }) => {
  const [selected, setSelected] = React.useState(undefined);
  const { options, searching, search } = useSkuSearch();
  const titles = useSkuTitles(skus);

  const chosen = skus.filter((entry) => entry.sku);
  const autoFill = Math.max(itemsLimit - chosen.length, 0);

  const addSku = (identifier) => {
    if (!identifier) return;
    if (chosen.some((entry) => entry.sku === identifier)) {
      setSelected(undefined);
      return;
    }

    onChange([...skus, makeBlockSku({ sku: identifier })]);
    setSelected(undefined);
    search("");
  };

  const moveSku = (key, offset) => {
    const index = skus.findIndex((entry) => entry.key === key);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= skus.length) return;

    const reordered = [...skus];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];
    onChange(reordered);
  };

  return (
    <div className="mb-3">
      <div className="font-weight-bold mb-2">Products</div>

      <Select
        className="w-100 mb-2"
        showSearch
        allowClear
        value={selected}
        placeholder="Search by SKU or title"
        filterOption={false}
        notFoundContent={searching ? <Spin size="small" /> : null}
        onSearch={search}
        onChange={addSku}
        onClear={() => search("")}
        options={options.map((product) => ({
          value: product.identifier,
          label: `${product.identifier} - ${product.title}`,
        }))}
      />

      {chosen.length ? (
        <div style={{ border: "1px solid #F0F0F0", borderRadius: 4 }}>
          {skus
            .filter((entry) => entry.sku)
            .map((entry, index) => (
              <SkuRow
                key={entry.key}
                sku={entry.sku}
                title={titles[entry.sku]}
                index={index}
                total={chosen.length}
                onMove={(offset) => moveSku(entry.key, offset)}
                onRemove={() =>
                  onChange(skus.filter((item) => item.key !== entry.key))
                }
              />
            ))}
        </div>
      ) : (
        <Empty
          className="my-2"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No products picked - the whole carousel is filled automatically"
        />
      )}

      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        {chosen.length > itemsLimit ? (
          <>
            Picked {chosen.length} products, but the carousel shows{" "}
            {itemsLimit} - the last {chosen.length - itemsLimit} will not be
            displayed.
          </>
        ) : autoFill ? (
          <>
            Picked products come first, the remaining {autoFill} spot
            {autoFill === 1 ? "" : "s"} are filled with the best sellers of the
            selected category.
          </>
        ) : (
          <>
            Picked products fill the whole carousel - the category is not used
            for automatic filling.
          </>
        )}
      </div>
    </div>
  );
};

export default BlockSkus;
